/**
 * Разговор ленты с расширением про биржевую ногу.
 *
 * Лента про расширение ничего не знает и знать не должна: она кладёт сообщение
 * в собственное окно, и если расширения нет — читать его некому. Это тот же
 * мостик, что у обещаний (`confirm/publish.ts`), с одной разницей: здесь нужен
 * ОТВЕТ. «Куплено» и «не куплено» — разные действия человека дальше, и молчание
 * вместо ответа означало бы «идите смотрите на биржу сами».
 *
 * Отсюда идентификатор запроса: сообщений в окне много, чужих среди них
 * большинство, и ответ обязан находиться по ключу, а не по порядку прихода.
 *
 * Отсюда же таймаут. Расширения может не быть вовсе, и тогда ждать нечего:
 * промах по времени — это ответ «расширение молчит», а не вечное «сейчас-сейчас»
 * на кнопке, за которой стоят деньги.
 */

import type { OrderPlan } from './order';

export const ASK = 'forq:poly:ask';
export const SAY = 'forq:poly:say';

/** Ответа нет — значит расширения нет. Дольше человек всё равно не ждёт. */
export const WAIT_MS = 15_000;

/**
 * `seen` — «что ты помнишь про эту попытку». Спрашивается после перезагрузки
 * страницы: лента при этом теряет память целиком, а деньги — нет. Без этого
 * вопроса человек после F5 видит нетронутую кнопку «купить» над ордером,
 * который уже ушёл.
 */
export type What = 'can' | 'buy' | 'seen' | 'money';

export interface Ask {
  id: string;
  what: What;
  plan?: OrderPlan;
  /** Ключ попытки: по нему расширение отличает повтор от нового ордера. */
  key?: string;
}

/**
 * Что расширение отвечает на «можешь ли».
 *
 * Адреса счёта здесь нет: чтобы его назвать, надо разбудить тяжёлый модуль
 * биржи, а этот вопрос задаётся на каждой раскрытой карточке. Счёт человек
 * видит в панели расширения, где он и нужен, — рядом с остатком.
 */
export interface Can {
  ready: boolean;
  /** Словами: чего не хватает. Пусто — всё на месте. */
  why: string;
}

/** Чем закончился ордер. `unknown` — не отказ, см. `extension/src/poly.ts`. */
export interface Done {
  state: 'done' | 'no' | 'unknown';
  say: string;
  shares?: number;
  spent?: number;
}

/**
 * Что расширение помнит про попытку. `had: false` — не было такой.
 *
 * Отличать «не было» от «не ответили» обязательно: первое разрешает нажать,
 * второе — нет.
 */
export type Seen = { had: false } | ({ had: true } & Done);

export const askEnvelope = (ask: Ask): { channel: string } & Ask => ({ channel: ASK, ...ask });

export const sayEnvelope = (
  id: string,
  answer: unknown,
): { channel: string; id: string; answer: unknown } => ({ channel: SAY, id, answer });

/** Разобрать вопрос. `null` — это не наше сообщение или оно кривое. */
export function parseAsk(data: unknown): Ask | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== ASK) return null;
  if (typeof body.id !== 'string' || body.id === '') return null;
  if (body.what !== 'can' && body.what !== 'buy' && body.what !== 'seen' && body.what !== 'money') {
    return null;
  }
  if (body.what === 'can' || body.what === 'money') return { id: body.id, what: body.what };
  if (typeof body.key !== 'string' || body.key === '') return null;
  if (body.what === 'seen') return { id: body.id, what: 'seen', key: body.key };
  const plan = parsePlan(body.plan);
  if (plan === null) return null;
  return { id: body.id, what: 'buy', plan, key: body.key };
}

/**
 * Разобрать ордер. Проверяется каждое поле, и это не паранойя формы: по этим
 * числам подписывается трата денег, а сообщение в окно может положить кто
 * угодно. Потолок цены обязан быть положительным и не меньше самой цены —
 * ордер с потолком ниже цены не исполнится никогда, и молча отправлять его
 * значило бы врать про попытку.
 */
export function parsePlan(raw: unknown): OrderPlan | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const one = raw as Record<string, unknown>;
  const nums = ['usd', 'price', 'maxPrice', 'dead', 'shares'] as const;
  for (const field of nums) {
    const value = one[field];
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  }
  const plan = one as unknown as OrderPlan;
  if (typeof plan.tokenId !== 'string' || plan.tokenId === '') return null;
  if (plan.maxPrice < plan.price || plan.maxPrice >= 1) return null;
  return {
    tokenId: plan.tokenId,
    usd: plan.usd,
    price: plan.price,
    maxPrice: plan.maxPrice,
    dead: plan.dead,
    shares: plan.shares,
  };
}

/** A filled order without finite execution amounts is unresolved, never an estimate. */
export function parseDone(raw: unknown): Done {
  if (typeof raw !== 'object' || raw === null) return { state: 'unknown', say: 'непонятный ответ расширения' };
  const one = raw as Partial<Done>;
  const say = typeof one.say === 'string' ? one.say : '';
  const shares = typeof one.shares === 'number' && Number.isFinite(one.shares) && one.shares > 0 ? one.shares : undefined;
  const spent = typeof one.spent === 'number' && Number.isFinite(one.spent) && one.spent > 0 ? one.spent : undefined;
  const empty = (value: unknown): boolean => value === undefined || value === 0;
  const state = one.state === 'done' && shares !== undefined && spent !== undefined ? 'done'
    : one.state === 'no' && empty(one.shares) && empty(one.spent) ? 'no' : 'unknown';
  return { state, say, ...(shares !== undefined ? { shares } : {}), ...(spent !== undefined ? { spent } : {}) };
}

/** Разобрать ответ расширения. `null` — не наш или не про этот вопрос. */
export function parseSay(data: unknown, id: string): unknown | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== SAY || body.id !== id) return null;
  return body.answer ?? null;
}

let counter = 0;
const nextId = (): string => `${Date.now().toString(36)}-${(counter += 1)}`;

/**
 * Спросить расширение и дождаться ответа. `null` — расширение молчит.
 *
 * Окно передаётся параметром: тесты гоняют разговор без браузера, а вкладка
 * ленты может быть открыта внутри чужого окна, и адресовать сообщение надо
 * своему.
 */
export async function ask(
  what: What,
  body: { plan?: OrderPlan; key?: string } = {},
  where: Window = window,
  wait = WAIT_MS,
): Promise<unknown | null> {
  const id = nextId();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      where.removeEventListener('message', hear);
      resolve(null);
    }, wait);
    function hear(event: MessageEvent): void {
      if (event.source !== where) return;
      const answer = parseSay(event.data, id);
      if (answer === null) return;
      clearTimeout(timer);
      where.removeEventListener('message', hear);
      resolve(answer);
    }
    where.addEventListener('message', hear);
    where.postMessage(askEnvelope({ id, what, ...body }), '*');
  });
}

/**
 * Остаток на бирже. Тяжёлый: грузит модуль подписи и бьёт CLOB.
 * Только по кнопке «проверить», не в тике карточки.
 */
export async function purse(where: Window = window, wait = WAIT_MS): Promise<unknown | null> {
  return ask('money', {}, where, wait);
}

/** Умеет ли расширение купить прямо сейчас. Дешёвый вопрос: без сети. */
export async function can(where: Window = window, wait = WAIT_MS): Promise<Can> {
  const answer = await ask('can', {}, where, wait);
  if (answer === null) return { ready: false, why: 'расширение не отвечает' };
  const one = answer as Partial<Can>;
  return { ready: one.ready === true, why: typeof one.why === 'string' ? one.why : '' };
}

/**
 * Что расширение помнит про эту попытку. Спрашивается при открытии карточки.
 *
 * Ждём не пятнадцать секунд, а две: это вопрос к памяти, а не к сети, и
 * молчание здесь значит «расширения нет» — на кнопку оно повлияет и без нас.
 */
export async function seen(key: string, where: Window = window): Promise<Seen> {
  const answer = await ask('seen', { key }, where, 2_000);
  if (typeof answer !== 'object' || answer === null) return { had: true, state: 'unknown', say: 'память ордеров не ответила' };
  if ((answer as Partial<Seen>).had === false) return { had: false };
  return { had: true, ...parseDone(answer) };
}

/** Купить биржевую ногу. Молчание расширения — это `unknown`, а не отказ. */
export async function buy(plan: OrderPlan, key: string, where: Window = window): Promise<Done> {
  const answer = await ask('buy', { plan, key }, where);
  if (answer === null) {
    // Расширение могло успеть отправить ордер и умереть вместе с окном. Сказать
    // «не куплено» здесь — значит позвать человека нажать второй раз поверх
    // возможной покупки.
    return { state: 'unknown', say: 'расширение не ответило, проверьте ордера на бирже' };
  }
  return parseDone(answer);
}
